import React, { useMemo } from "react";
import { Avatar, IconButton, Paper, Tooltip } from "@mui/material";
import { ContentCopyRounded, DesktopWindowsRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "controls";

/** The only thing this page will pass on: ERP1. and base64url, nothing else. */
const KEY_SHAPE = /^ERP1\.[A-Za-z0-9_\-=]{8,1024}$/;

/**
 * Connect this computer — the page a "Connect link" opens on the counter PC.
 *
 * Public, because the counter PC is not signed in to the web app, and it has to be: the whole
 * point is that nobody copies a long key between machines. The key rides in the URL FRAGMENT
 * (after #), which a browser never sends to any server and never puts in a Referer, so it is not
 * in the web server's logs and this page makes no request with it.
 *
 * All it does is hand the key to the desktop app through the biz: link the installer registers.
 * BIZ fills its Setup Key box and waits for a person to press Connect.
 */
const ConnectDesktop = () => {
  const { t } = useTranslation(["common"]);

  const key = useMemo(() => {
    try {
      const raw = decodeURIComponent((window.location.hash || "").replace(/^#/, "")).trim();
      return KEY_SHAPE.test(raw) ? raw : null;
    } catch {
      return null; // a mangled % sequence is an incomplete link, not a crash
    }
  }, []);

  const copy = () =>
    navigator.clipboard?.writeText(key).then(
      () => toast.success(t("copied", "{{what}} copied", { what: t("appSetupKey", "Setup key") })),
      () => toast.error(t("copyFailed", "Could not copy — select it by hand.")),
    );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Paper elevation={0} className="w-full max-w-[460px] border rounded-xl p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Avatar variant="rounded" sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
            <DesktopWindowsRounded className="!text-[22px]" />
          </Avatar>
          <div className="text-[17px] font-semibold">{t("connectPcTitle", "Connect this computer to BIZ")}</div>
        </div>

        {key ? (
          <>
            <div className="text-[13px] text-gray-600">
              {t("connectPcIntro", "BIZ must already be installed on this computer.")}
            </div>

            <Button className="w-full" onClick={() => (window.location.href = `biz://connect/${key}`)}>
              {t("connectPcOpen", "Open BIZ")}
            </Button>

            <div className="text-[13px] text-gray-600">
              {t(
                "connectPcAfter",
                "BIZ opens with the setup key filled in. Check the server name, then press Connect.",
              )}
            </div>

            <div className="border-t pt-3 flex flex-col gap-2">
              <div className="text-[12px] text-gray-500">
                {t("connectPcFallback", "Nothing happened? Copy the key and paste it into the Setup Key box in BIZ.")}
              </div>
              <div className="flex items-start gap-2 min-w-0">
                <code className="font-mono text-[11px] leading-snug break-all bg-gray-50 border rounded-lg p-2 flex-1 min-w-0">
                  {key}
                </code>
                <Tooltip title={t("connectPcCopyKey", "Copy setup key")}>
                  <IconButton size="small" onClick={copy}>
                    <ContentCopyRounded className="!text-[16px]" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </>
        ) : (
          <div className="text-[13px] text-red-600">
            {t("connectPcBadLink", "This link is incomplete. Ask for a new one.")}
          </div>
        )}
      </Paper>
    </div>
  );
};

export default ConnectDesktop;
